import * as React from 'react';
import {
    Checkbox,
    Dropdown,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Text,
    TooltipHost,
} from '@fluentui/react';
import type {
    AgentTaskProposalVote,
    AgentTaskProposedChangeResult,
    AgentTaskProposedChangeResultSet,
    AgentTaskQuestionSourceLocation,
} from '../types';
import { AgentTaskProposalVote as ProposalVote } from '../types';
import './TaskScopedProposalReview.css';

export const PROPOSAL_RATIONALE_LABELS = [
    'Recipe',
    'Classification',
    'Killswitch',
    'Original behavior',
    'Optimized behavior',
    'Edge cases',
    'Tests',
    'Anti-pattern safety',
] as const;

export type ProposalRationaleLabel = typeof PROPOSAL_RATIONALE_LABELS[number];

export interface ProposalRationalePart {
    label?: ProposalRationaleLabel;
    text: string;
    raw: string;
    sourceIndex: number;
}

export interface ParsedProposalRationale {
    originalText: string;
    parts: ProposalRationalePart[];
}

const INLINE_RATIONALE_LABELS = new Set<ProposalRationaleLabel>([
    'Recipe',
    'Classification',
    'Killswitch',
]);

const RATIONALE_LABEL_LOOKUP = new Map(
    PROPOSAL_RATIONALE_LABELS.map(label => [label.toLocaleLowerCase(), label]),
);

const RATIONALE_LABEL_PATTERN = new RegExp(
    `(${PROPOSAL_RATIONALE_LABELS
        .map(label => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|')})\\s*:`,
    'gi',
);

const VOTE_OPTIONS: IDropdownOption[] = [
    { key: 'unvoted', text: 'Unvoted' },
    { key: ProposalVote.Ok, text: 'Ok' },
    { key: ProposalVote.Blocked, text: 'Blocked' },
    { key: ProposalVote.TooSmall, text: 'TooSmall' },
    { key: ProposalVote.Deferred, text: 'Deferred' },
];

const VOTE_CLASS_NAMES: Record<string, string> = {
    unvoted: 'atmProposalReview__voteCell--unvoted',
    [ProposalVote.Ok]: 'atmProposalReview__voteCell--ok',
    [ProposalVote.Blocked]: 'atmProposalReview__voteCell--blocked',
    [ProposalVote.TooSmall]: 'atmProposalReview__voteCell--tooSmall',
    [ProposalVote.Deferred]: 'atmProposalReview__voteCell--deferred',
};

const SELECTIVE_SUBMISSION_HELP =
    'If any changes are checked, only those changes will be submitted. '
    + 'If none are checked, all applicable Ok changes will be submitted.';

function trimRationaleText(text: string) {
    const leadingLineBreak = /^[ \t]*\r?\n/.exec(text);
    return leadingLineBreak ? text.slice(leadingLineBreak[0].length).trimEnd() : text.trim();
}

export function parseProposalRationale(rationale?: string | null): ParsedProposalRationale {
    const originalText = rationale ?? '';
    if (!originalText) {
        return { originalText, parts: [] };
    }

    const matches = Array.from(originalText.matchAll(RATIONALE_LABEL_PATTERN));
    if (matches.length === 0) {
        return {
            originalText,
            parts: [{
                text: trimRationaleText(originalText),
                raw: originalText,
                sourceIndex: 0,
            }],
        };
    }

    const parts: ProposalRationalePart[] = [];
    const firstIndex = matches[0].index ?? 0;
    if (firstIndex > 0) {
        const raw = originalText.slice(0, firstIndex);
        parts.push({
            text: trimRationaleText(raw),
            raw,
            sourceIndex: 0,
        });
    }

    matches.forEach((match, index) => {
        const sourceIndex = match.index ?? 0;
        const endIndex = matches[index + 1]?.index ?? originalText.length;
        const raw = originalText.slice(sourceIndex, endIndex);
        const matchedLabel = match[1].toLocaleLowerCase();
        parts.push({
            label: RATIONALE_LABEL_LOOKUP.get(matchedLabel),
            text: trimRationaleText(originalText.slice(sourceIndex + match[0].length, endIndex)),
            raw,
            sourceIndex,
        });
    });

    return { originalText, parts };
}

export function reconstructProposalRationale(parsed: ParsedProposalRationale) {
    return parsed.parts.map(part => part.raw).join('');
}

export function calculateProposalSnapshotReduction(
    taskReductionPercent?: number | null,
    taskSnapshotInclusivePercent?: number | null,
) {
    if (taskReductionPercent === undefined
        || taskReductionPercent === null
        || taskSnapshotInclusivePercent === undefined
        || taskSnapshotInclusivePercent === null) {
        return undefined;
    }

    return taskReductionPercent * taskSnapshotInclusivePercent / 100;
}

export function getOkAllProposalVote(
    change: AgentTaskProposedChangeResult,
    taskSnapshotInclusivePercent?: number | null,
) {
    const snapshotReduction = calculateProposalSnapshotReduction(
        change.expectedReductionPercent,
        taskSnapshotInclusivePercent,
    );
    return snapshotReduction !== undefined
        && Math.round(snapshotReduction * 100) === 0
        ? ProposalVote.TooSmall
        : ProposalVote.Ok;
}

export function isProposalSelectable(change: AgentTaskProposedChangeResult) {
    return change.isCurrent
        && change.outcome !== 'discarded'
        && change.vote === ProposalVote.Ok;
}

export function getCurrentActiveProposals(
    proposals: readonly AgentTaskProposedChangeResult[],
) {
    return proposals.filter(change => change.isCurrent && change.outcome !== 'discarded');
}

function formatReduction(value?: number | null) {
    return value === undefined || value === null
        ? 'Not estimated'
        : `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}%`;
}

function getVoteLabel(vote: AgentTaskProposalVote | null) {
    return VOTE_OPTIONS.find(option => option.key === (vote ?? 'unvoted'))?.text ?? 'Unvoted';
}

function formatLocation(location: AgentTaskQuestionSourceLocation, index: number) {
    const lines = location.startLine === undefined
        ? undefined
        : location.endLine !== undefined && location.endLine !== location.startLine
            ? `lines ${location.startLine}–${location.endLine}`
            : `line ${location.startLine}`;
    return [
        location.repository,
        location.project,
        location.path,
        location.symbol,
        lines,
    ].filter(Boolean).join(' • ') || `Source ${index + 1}`;
}

function ProposalRationale({ rationale }: { rationale?: string | null }) {
    const parsed = parseProposalRationale(rationale);
    if (parsed.parts.length === 0) {
        return null;
    }

    return (
        <div className="atmProposalReview__rationale">
            {parsed.parts.map(part => {
                if (!part.text) {
                    return null;
                }

                if (part.label && INLINE_RATIONALE_LABELS.has(part.label)) {
                    return (
                        <span
                            className="atmProposalReview__rationaleInline"
                            key={`${part.sourceIndex}:${part.label}`}
                        >
                            {part.label}: {part.text}
                        </span>
                    );
                }

                return (
                    <p
                        className="atmProposalReview__rationaleParagraph"
                        key={`${part.sourceIndex}:${part.label ?? 'text'}`}
                    >
                        {part.label && <><u>{part.label}</u>: </>}
                        {part.text}
                    </p>
                );
            })}
        </div>
    );
}

function VerifiedSources({ locations }: { locations: readonly AgentTaskQuestionSourceLocation[] }) {
    return (
        <details className="atmProposalReview__sources">
            <summary>
                <span className="atmProposalReview__sourceIndicator" aria-hidden="true">▸</span>
                Verified sources ({locations.length})
            </summary>
            {locations.length > 0 && (
                <ul className="atmProposalReview__sourceList">
                    {locations.map((location, index) => (
                        <li key={`${location.codeUrl ?? location.path ?? 'source'}:${location.startLine ?? ''}:${index}`}>
                            {location.codeUrl ? (
                                <a href={location.codeUrl} target="_blank" rel="noopener noreferrer">
                                    {formatLocation(location, index)}
                                </a>
                            ) : formatLocation(location, index)}
                            {location.ref && <div><Text variant="small">Ref: {location.ref}</Text></div>}
                            {location.snippet && (
                                <pre className="atmProposalReview__snippet">
                                    <code>{location.snippet}</code>
                                </pre>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </details>
    );
}

function ProposalReviewRow({
    change,
    taskSnapshotInclusivePercent,
    readOnly,
    updating,
    selected,
    onVoteChange,
    onSelectionChange,
}: {
    change: AgentTaskProposedChangeResult;
    taskSnapshotInclusivePercent?: number | null;
    readOnly: boolean;
    updating: boolean;
    selected: boolean;
    onVoteChange?: (change: AgentTaskProposedChangeResult, vote: AgentTaskProposalVote | null) => void;
    onSelectionChange?: (change: AgentTaskProposedChangeResult, selected: boolean) => void;
}) {
    const voteKey = change.vote ?? 'unvoted';
    const snapshotReduction = calculateProposalSnapshotReduction(
        change.expectedReductionPercent,
        taskSnapshotInclusivePercent,
    );
    const selectable = !readOnly && isProposalSelectable(change);
    const readOnlyLabel = change.outcome === 'discarded' ? 'Discarded' : 'History';
    const proposalDescription = change.summary || change.text || 'Proposed change';
    const proposalState = readOnly ? readOnlyLabel : 'Current';

    return (
        <article
            className={`atmProposalReview__card${readOnly ? ' atmProposalReview__card--readonly' : ''}`}
            aria-label={`${proposalState} proposal: ${proposalDescription}`}
        >
            <div className={`atmProposalReview__voteCell ${VOTE_CLASS_NAMES[voteKey]}`}>
                {readOnly || !onVoteChange ? (
                    <div>
                        <Text variant="small">{readOnly ? readOnlyLabel : 'Vote'}</Text>
                        <div><strong>{getVoteLabel(change.vote)}</strong></div>
                    </div>
                ) : (
                    <Dropdown
                        ariaLabel={`Vote for proposed change: ${proposalDescription}`}
                        options={VOTE_OPTIONS}
                        selectedKey={voteKey}
                        disabled={updating}
                        onChange={(_, option) => {
                            const vote = option?.key === 'unvoted'
                                ? null
                                : Number(option?.key) as AgentTaskProposalVote;
                            onVoteChange(change, vote);
                        }}
                    />
                )}
                {!readOnly && onSelectionChange && (
                    <TooltipHost content={SELECTIVE_SUBMISSION_HELP}>
                        <Checkbox
                            ariaLabel={`Include proposed change in enqueue selection: ${proposalDescription}. ${SELECTIVE_SUBMISSION_HELP}`}
                            checked={selected}
                            disabled={!selectable || updating}
                            onChange={(_, checked) => onSelectionChange(change, checked ?? false)}
                        />
                    </TooltipHost>
                )}
            </div>
            <div className="atmProposalReview__description">
                <div className="atmProposalReview__summaryText">{proposalDescription}</div>
                <ProposalRationale rationale={change.rationale} />
                <div
                    className="atmProposalReview__impact"
                    role="group"
                    aria-label={`Expected reductions for proposed change: ${proposalDescription}`}
                >
                    <span><strong>Expected task reduction:</strong> {formatReduction(change.expectedReductionPercent)}</span>
                    <span><strong>Expected snapshot reduction:</strong> {formatReduction(snapshotReduction)}</span>
                </div>
                <VerifiedSources locations={change.locations} />
            </div>
        </article>
    );
}

export type ProposalVoteChangeHandler = (
    change: AgentTaskProposedChangeResult,
    vote: AgentTaskProposalVote | null,
) => void;

export type ProposalSelectionChangeHandler = (
    change: AgentTaskProposedChangeResult,
    selected: boolean,
) => void;

export interface TaskScopedProposalReviewProps {
    resultSet: AgentTaskProposedChangeResultSet;
    currentProposals?: readonly AgentTaskProposedChangeResult[];
    onVoteChange?: ProposalVoteChangeHandler;
    onSelectionChange?: ProposalSelectionChangeHandler;
    updatingProposalIds?: ReadonlySet<number>;
    selectedProposalIds?: ReadonlySet<number>;
    onOkAll?: () => void;
    okAllLoading?: boolean;
}

export function TaskScopedProposalReview({
    resultSet,
    currentProposals,
    onVoteChange,
    onSelectionChange,
    updatingProposalIds = new Set<number>(),
    selectedProposalIds = new Set<number>(),
    onOkAll,
    okAllLoading = false,
}: TaskScopedProposalReviewProps) {
    const suppliedCurrent = currentProposals ?? resultSet.proposedChangeEvents;
    const activeProposals = getCurrentActiveProposals(suppliedCurrent);
    const activeIds = new Set(activeProposals.map(change => change.analysisItemId));
    const contextProposals = resultSet.proposedChangeEvents.filter(change =>
        !activeIds.has(change.analysisItemId)
        && (!change.isCurrent || change.outcome === 'discarded'),
    );
    const hasUnvoted = activeProposals.some(change => change.vote === null);
    const allRowsUpdating = updatingProposalIds.size > 0;

    return (
        <section
            className="atmProposalReview"
            aria-label={`Proposal review for ${resultSet.taskScope}`}
        >
            <div
                className={`atmProposalReview__header${onOkAll ? '' : ' atmProposalReview__header--summaryOnly'}`}
                role="group"
                aria-label="Proposal review actions and summary"
            >
                {onOkAll && (
                    <div className="atmProposalReview__bulkAction">
                        <TooltipHost content="Vote all unvoted proposed changes. Changes with 0% expected snapshot reduction are marked TooSmall; the rest are marked Ok.">
                            <PrimaryButton
                                text={okAllLoading ? 'Marking...' : 'Ok all'}
                                ariaLabel={okAllLoading ? 'Marking proposed changes Ok...' : 'Ok all proposed changes'}
                                disabled={okAllLoading || allRowsUpdating || !hasUnvoted}
                                onClick={onOkAll}
                            />
                        </TooltipHost>
                    </div>
                )}
                <div className="atmProposalReview__summary">
                    {resultSet.resultSummary && <p><strong>Summary:</strong> {resultSet.resultSummary}</p>}
                    {resultSet.noChangeReason && (
                        <div className="atmProposalReview__noChange" role="status">
                            <strong>No-change reason:</strong> {resultSet.noChangeReason}
                        </div>
                    )}
                </div>
            </div>

            {activeProposals.length > 0 ? (
                <div className="atmProposalReview__rows">
                    {activeProposals.map(change => (
                        <ProposalReviewRow
                            key={change.analysisItemId}
                            change={change}
                            taskSnapshotInclusivePercent={resultSet.taskSnapshotInclusivePercent}
                            readOnly={false}
                            updating={updatingProposalIds.has(change.analysisItemId)}
                            selected={selectedProposalIds.has(change.analysisItemId)}
                            onVoteChange={onVoteChange}
                            onSelectionChange={onSelectionChange}
                        />
                    ))}
                </div>
            ) : (
                <MessageBar messageBarType={MessageBarType.info}>
                    No current active proposals were recorded.
                </MessageBar>
            )}

            {contextProposals.length > 0 && (
                <section className="atmProposalReview__context" aria-label="Discarded and proposal history">
                    <Text variant="mediumPlus">Discarded and history</Text>
                    <p>Read-only proposals below are not part of the current active review.</p>
                    <div className="atmProposalReview__rows">
                        {contextProposals.map(change => (
                            <ProposalReviewRow
                                key={change.analysisItemId}
                                change={change}
                                taskSnapshotInclusivePercent={resultSet.taskSnapshotInclusivePercent}
                                readOnly
                                updating={false}
                                selected={false}
                            />
                        ))}
                    </div>
                </section>
            )}

            {resultSet.assumptions.length > 0 && (
                <section className="atmProposalReview__assumptions" aria-label="Proposal assumptions">
                    <strong>Assumptions</strong>
                    <ul>
                        {resultSet.assumptions.map((assumption, index) => (
                            <li key={`${index}:${assumption}`}>{assumption}</li>
                        ))}
                    </ul>
                </section>
            )}
        </section>
    );
}
